import useDuplicateProposal from '@/api/mutations/proposals/useDuplicateProposal';
import { type KnownCustomer } from '@/ui/components/customerSearch/useCustomerSearch';
import { useCustomerSearchField } from '@/ui/components/customerSearch/useCustomerSearchField';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Combobox,
  Field,
  Form,
  Modal,
  ModalProps,
  Text,
  useToastContext,
} from '@payaca/ambrosia';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod/v4';

const duplicateProposalSchema = z.object({
  customerId: z
    .number()
    .nullable()
    .refine((customerId) => customerId != null, {
      message: 'Select a customer for the copy',
    }),
});

type TFormValues = z.infer<typeof duplicateProposalSchema>;

type DuplicateProposalModalProps = {
  proposalId: string;
  /** The Project's Customer, which the copy defaults to. */
  defaultCustomer: KnownCustomer | undefined;
  /** Called with the copy's Project and Proposal public ids. */
  onDuplicated: (copy: { projectId: string; proposalId: string }) => void;
} & Omit<ModalProps, 'title' | 'children'>;

/**
 * A duplicate lands on a freshly created Project, so it needs a Customer for
 * that Project. Defaults to this Project's Customer, which is the common case.
 */
const DuplicateProposalModal: FC<DuplicateProposalModalProps> = (props) => {
  const {
    proposalId,
    defaultCustomer,
    onDuplicated,
    isOpen,
    onClose,
    ...rest
  } = props;
  const { pushSuccess } = useToastContext();

  const { mutate, isPending } = useDuplicateProposal({
    options: {
      onSuccess: (data) => {
        const { publicId, project } = data.duplicateProposal;
        pushSuccess(
          project.customer
            ? `Proposal duplicated onto a new project for ${project.customer.name}.`
            : 'Proposal duplicated onto a new project.'
        );
        onClose?.();
        onDuplicated({ projectId: project.publicId, proposalId: publicId });
      },
    },
  });

  return (
    <Modal
      {...rest}
      isOpen={isOpen}
      onClose={onClose}
      title="Duplicate proposal"
      // ModalCore gates outside-press and Escape together, and defaults to
      // suppressing both. Losing a picker selection to Escape is cheap.
      disableBackdropClick={false}
    >
      {/* Remounted as the modal opens so the picker re-seeds from the Project's
          Customer, which arrives with its query rather than at mount. */}
      <DuplicateProposalForm
        key={String(isOpen)}
        defaultCustomer={defaultCustomer}
        onClose={onClose}
        isPending={isPending}
        onSubmit={({ customerId }) =>
          mutate({ proposalId, customerId: String(customerId) })
        }
      />
    </Modal>
  );
};

type DuplicateProposalFormProps = {
  /** The Project's Customer, which the copy defaults to. */
  defaultCustomer: KnownCustomer | undefined;
  onClose: (() => void) | undefined;
  isPending: boolean;
  onSubmit: (values: { customerId: number }) => void;
};

const DuplicateProposalForm: FC<DuplicateProposalFormProps> = (props) => {
  const { defaultCustomer, onClose, isPending, onSubmit } = props;

  const formMethods = useForm<TFormValues>({
    defaultValues: { customerId: defaultCustomer?.id ?? null },
    resolver: zodResolver(duplicateProposalSchema),
  });

  const handleSubmit = (values: TFormValues) => {
    if (values.customerId == null) return;
    onSubmit({ customerId: values.customerId });
  };

  return (
    <Form methods={formMethods} onSubmit={handleSubmit}>
      <Modal.Body>
        <Text>This copies the proposal onto a new project as a draft.</Text>

        <CustomerField knownCustomer={defaultCustomer} disabled={isPending} />
      </Modal.Body>

      <Modal.Footer>
        <Modal.Footer.Actions>
          <Button variant="secondary" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" isProcessing={isPending} disabled={isPending}>
            Duplicate
          </Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Form>
  );
};

/** Reads the form from context, so it renders inside `Form`. */
const CustomerField: FC<{
  knownCustomer: KnownCustomer | undefined;
  disabled: boolean;
}> = (props) => {
  const { knownCustomer, disabled } = props;

  const customerField = useCustomerSearchField<TFormValues, 'customerId'>({
    name: 'customerId',
    knownCustomer,
  });

  return (
    <Field name="customerId">
      <Field.Label>Customer</Field.Label>
      <Combobox {...customerField} disabled={disabled} />
    </Field>
  );
};

export default DuplicateProposalModal;
